import React, { useState } from 'react';
import { ApiKeyInfo, FreeTierInfo, ModelInfo } from './types';
import ModelPicker from './ModelPicker';

interface ApiKeyCardProps {
  apiKey: ApiKeyInfo | null;
  freeTier: FreeTierInfo | null; // when enabled, a key is an optional upgrade, not a requirement
  models: ModelInfo[];
  defaultModel: string;
  // Returns an error message to display, or null on success.
  onSave: (key: string, model: string) => Promise<string | null>;
  onDelete: () => Promise<void>;
}

// Add / replace / delete the user's Anthropic API key. The key is write-only:
// after saving, only the last 4 characters ever come back from the server.
const ApiKeyCard: React.FC<ApiKeyCardProps> = ({ apiKey, freeTier, models, defaultModel, onSave, onDelete }) => {
  const freeTierOn = !!freeTier?.enabled;
  const [editing, setEditing] = useState(false);
  const [keyInput, setKeyInput] = useState('');
  const [modelChoice, setModelChoice] = useState(apiKey?.default_model || defaultModel);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const showForm = editing || !apiKey;

  const handleSave = async () => {
    setBusy(true);
    setError(null);
    const message = await onSave(keyInput.trim(), modelChoice);
    setBusy(false);
    if (message) {
      setError(message);
    } else {
      setKeyInput('');
      setEditing(false);
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    await onDelete();
    setBusy(false);
    setConfirmDelete(false);
  };

  return (
    <section className="settings-card">
      <h3>Anthropic API Key{freeTierOn && !apiKey ? ' (optional)' : ''}</h3>

      {apiKey && !showForm && (
        <div className="settings-key-status">
          <p>
            <code>sk-ant-…{apiKey.last4}</code>{' '}
            <span className={`settings-badge settings-badge-${apiKey.status}`}>
              {apiKey.status === 'valid' ? 'Valid' : apiKey.status === 'invalid' ? 'Invalid — replace it' : 'Not yet validated'}
            </span>
          </p>
          {apiKey.status === 'invalid' && (
            <p className="settings-error">
              Anthropic rejected this key on a recent request. Paste a new key to restore AI features
              {freeTierOn ? ', or remove it to go back to the free tier' : ''}.
            </p>
          )}
          <div className="settings-key-actions">
            <button className="secondary-button" onClick={() => { setEditing(true); setError(null); }} disabled={busy}>
              Replace Key
            </button>
            {confirmDelete ? (
              <>
                <button className="delete-button" onClick={handleDelete} disabled={busy}>
                  {busy ? 'Removing…' : 'Confirm Remove'}
                </button>
                <button className="secondary-button" onClick={() => setConfirmDelete(false)} disabled={busy}>
                  Cancel
                </button>
              </>
            ) : (
              <button className="delete-button" onClick={() => setConfirmDelete(true)} disabled={busy}>
                Remove Key
              </button>
            )}
          </div>
        </div>
      )}

      {showForm && (
        <div className="settings-key-form">
          {!apiKey && (
            <div className="settings-walkthrough">
              {freeTierOn ? (
                <>
                  <p>
                    <strong>You're on the free tier.</strong> AI features run on {freeTier?.modelLabel} at no cost
                    to you, up to a daily limit.
                  </p>
                  <p>
                    Add your own Anthropic API key to choose any model, including Opus, with no daily limit.
                    Usage then bills to your own Anthropic account. One-time setup:
                  </p>
                </>
              ) : (
                <p>AI features run on your own Anthropic account, so you control the spend. One-time setup:</p>
              )}
              <ol>
                <li>
                  Create an account at{' '}
                  <a href="https://console.anthropic.com" target="_blank" rel="noopener noreferrer">console.anthropic.com</a>
                </li>
                <li>Add credits under <strong>Billing</strong> ($5–10 goes a long way)</li>
                <li>Create a key under <strong>API Keys</strong> and paste it below</li>
              </ol>
              <p className="settings-hint">
                Your key is encrypted before storage, never shown again after saving, and only used to run your own requests.
              </p>
            </div>
          )}

          <div className="form-group">
            <label>API Key</label>
            <input
              type="password"
              value={keyInput}
              onChange={(e) => { setKeyInput(e.target.value); setError(null); }}
              placeholder="sk-ant-…"
              autoComplete="off"
            />
          </div>

          <div className="form-group">
            <label>Model</label>
            <ModelPicker models={models} value={modelChoice} onChange={setModelChoice} disabled={busy} />
          </div>

          {error && <p className="settings-error">{error}</p>}

          <div className="settings-key-actions">
            <button
              className="primary-button"
              onClick={handleSave}
              disabled={busy || !keyInput.trim().startsWith('sk-ant-')}
            >
              {busy ? 'Validating…' : 'Save & Validate Key'}
            </button>
            {apiKey && (
              <button className="secondary-button" onClick={() => { setEditing(false); setError(null); }} disabled={busy}>
                Cancel
              </button>
            )}
          </div>
          <p className="settings-hint">
            Saving runs a tiny live test against Anthropic to confirm the key and model work (costs a fraction of a cent).
          </p>
        </div>
      )}
    </section>
  );
};

export default ApiKeyCard;
